import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { getCategories, saveCategory, deleteCategory } from '../../data/storeData';

const inputClass = "w-full px-4 py-2.5 bg-[#fbfbfd] border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-transparent transition-all placeholder:text-gray-400 disabled:bg-gray-100 disabled:text-gray-400";
const labelClass = "block text-[13px] font-semibold text-gray-700 mb-1.5";
const subInputClass = "px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/60";

const CategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    icon: '',
    order: 0,
    subcategories: []
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const cats = await getCategories();
      // Sort by order
      const sorted = (cats || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(sorted);
    } catch (error) {
      console.error('Error loading categories:', error);
      alert('Error loading categories');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) || 0 : value
    }));
  };

  const addSubcategory = () => {
    setFormData(prev => ({
      ...prev,
      subcategories: [...prev.subcategories, { id: '', name: '', icon: '', order: 0 }]
    }));
  };

  const updateSubcategory = (index, field, value) => {
    const newSubs = [...formData.subcategories];
    newSubs[index][field] = field === 'order' ? parseInt(value) || 0 : value;
    setFormData(prev => ({ ...prev, subcategories: newSubs }));
  };

  const removeSubcategory = (index) => {
    setFormData(prev => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.id) {
      alert('Please fill in category name and ID');
      return;
    }

    // Validate subcategory IDs
    for (const sub of formData.subcategories) {
      if (!sub.id || !sub.name) {
        alert('All subcategories must have ID and name');
        return;
      }
    }

    // Sort subcategories by order
    const sortedSubs = formData.subcategories.sort((a, b) => (a.order || 0) - (b.order || 0));

    try {
      await saveCategory({ ...formData, subcategories: sortedSubs });
      await loadCategories();
      resetForm();
      alert(editingCategory ? 'Category updated!' : 'Category added!');
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error saving category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      ...category,
      subcategories: category.subcategories || [],
      order: category.order || 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Delete this category? Products will remain but may not show correctly.')) {
      return;
    }

    try {
      await deleteCategory(categoryId);
      await loadCategories();
      alert('Category deleted!');
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category');
    }
  };

  // Move category up/down
  const moveCategory = async (index, direction) => {
    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newCategories.length) return;

    // Swap orders
    const tempOrder = newCategories[index].order || index;
    newCategories[index].order = newCategories[targetIndex].order || targetIndex;
    newCategories[targetIndex].order = tempOrder;

    // Save both
    try {
      await saveCategory(newCategories[index]);
      await saveCategory(newCategories[targetIndex]);
      await loadCategories();
    } catch (error) {
      console.error('Error moving category:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      icon: '',
      order: 0,
      subcategories: []
    });
    setEditingCategory(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add/Edit Form */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] p-6 md:p-8">
        <div className="mb-7">
          <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
          <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h2>
          {editingCategory && (
            <p className="text-sm text-gray-500 mt-1">Editing: {editingCategory.name}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>
                Category ID * <span className="text-gray-400 font-normal">(lowercase, no spaces)</span>
              </label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleChange}
                required
                disabled={!!editingCategory}
                className={inputClass}
                placeholder="apple"
              />
              {editingCategory && (
                <p className="text-xs text-gray-400 mt-1.5">ID cannot be changed</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Category Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="Apple"
              />
            </div>

            <div>
              <label className={labelClass}>Display Order</label>
              <input
                type="number"
                name="order"
                value={formData.order}
                onChange={handleChange}
                min="0"
                max="100"
                className={inputClass}
                placeholder="1"
              />
              <p className="text-xs text-gray-400 mt-1.5">Lower numbers appear first</p>
            </div>

            <div className="md:col-span-3">
              <label className={labelClass}>Icon <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                className={inputClass}
                placeholder="Leave empty"
              />
            </div>
          </div>

          {/* Subcategories */}
          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#1d1d1f] tracking-tight">Subcategories</h3>
                <p className="text-sm text-gray-400">Groups shown inside this category</p>
              </div>
              <button
                type="button"
                onClick={addSubcategory}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 text-sm font-semibold transition-all active:scale-95 shadow-[0_8px_20px_-6px_rgba(249,115,22,0.4)]"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Add Subcategory
              </button>
            </div>

            <div className="space-y-3">
              {formData.subcategories.map((sub, index) => (
                <div key={index} className="bg-[#fbfbfd] border border-gray-100 p-4 rounded-2xl">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-gray-700">Subcategory {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeSubcategory(index)}
                      className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      value={sub.id}
                      onChange={(e) => updateSubcategory(index, 'id', e.target.value)}
                      placeholder="ID (iphone)"
                      className={subInputClass}
                    />
                    <input
                      type="text"
                      value={sub.name}
                      onChange={(e) => updateSubcategory(index, 'name', e.target.value)}
                      placeholder="Name (iPhone)"
                      className={subInputClass}
                    />
                    <input
                      type="number"
                      value={sub.order || 0}
                      onChange={(e) => updateSubcategory(index, 'order', e.target.value)}
                      placeholder="Order"
                      min="0"
                      className={subInputClass}
                    />
                    <input
                      type="text"
                      value={sub.icon}
                      onChange={(e) => updateSubcategory(index, 'icon', e.target.value)}
                      placeholder="Icon"
                      className={subInputClass}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#1d1d1f] text-white rounded-full hover:bg-black font-semibold transition-all active:scale-95 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.35)]"
            >
              <Save className="w-4 h-4" strokeWidth={1.75} />
              {editingCategory ? 'Update Category' : 'Add Category'}
            </button>

            {editingCategory && (
              <button
                type="button"
                onClick={resetForm}
                className="px-7 py-3 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 font-semibold transition-all active:scale-95"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
            <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">All Categories</h2>
          </div>
          <span className="px-3.5 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm font-bold">
            {categories.length}
          </span>
        </div>

        <div className="space-y-4">
          {categories.map((category, index) => (
            <div key={category.id} className="border border-gray-100 rounded-2xl p-5 bg-[#fbfbfd] hover:bg-white hover:shadow-[0_12px_28px_-12px_rgba(0,0,0,0.12)] transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveCategory(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-[#1d1d1f] hover:bg-gray-100 rounded-md disabled:opacity-20 transition-colors"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveCategory(index, 'down')}
                      disabled={index === categories.length - 1}
                      className="p-1 text-gray-400 hover:text-[#1d1d1f] hover:bg-gray-100 rounded-md disabled:opacity-20 transition-colors"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md">
                        #{category.order || 0}
                      </span>
                      <h3 className="text-lg font-bold text-[#1d1d1f] tracking-tight">{category.name}</h3>
                    </div>
                    <p className="text-sm text-gray-400 mt-0.5">ID: {category.id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleEdit(category)}
                    className="p-2.5 text-gray-400 hover:text-[#1d1d1f] hover:bg-gray-100 rounded-xl transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              </div>

              {/* Subcategories */}
              {category.subcategories && category.subcategories.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-gray-400 mb-2.5">
                    Subcategories ({category.subcategories.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {category.subcategories
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map(sub => (
                        <span
                          key={sub.id}
                          className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-full text-sm font-medium"
                        >
                          {sub.order !== undefined && (
                            <span className="text-xs text-orange-500 font-bold mr-1.5">#{sub.order}</span>
                          )}
                          {sub.name}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoriesTab;
